import { LANGUAGE_INFO, Language } from "@salon/types";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSalonSlug } from "@/hooks/use-salon-slug";
import { useSalonStore } from "@/lib/store/salon";

// Utility function to generate salon slug from name
function _generateSalonSlug(name: string): string {
  if (!name || typeof name !== "string") {
    return "";
  }

  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Replace multiple hyphens with single hyphen
}

// Utility function to build storefront URL
function buildStorefrontUrl(
  language: Language,
  salonSlug: string,
  trafficSourceCode?: string
): string {
  const baseUrl = import.meta.env.VITE_STOREFRONT_URL || "http://localhost:3230";
  let url = `${baseUrl}/${language}/${salonSlug}`;

  if (trafficSourceCode) {
    url += `?utm_source=${encodeURIComponent(trafficSourceCode)}`;
  }

  return url;
}

interface StorefrontUrlCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultLanguage?: Language;
  trafficSourceCode?: string;
  trafficSourceName?: string;
}

export function StorefrontUrlCopyDialog({
  open,
  onOpenChange,
  defaultLanguage,
  trafficSourceCode,
  trafficSourceName,
}: StorefrontUrlCopyDialogProps) {
  const { t } = useTranslation("settings");

  const currentSalon = useSalonStore((state) => state.currentSalon);
  const salonDefaultLanguage = currentSalon?.defaultLanguage || Language.EN;

  // Fetch salon slug from database
  const { slug: realSlug, loading: loadingSlug } = useSalonSlug(currentSalon?.id);

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    (defaultLanguage ?? salonDefaultLanguage) as Language
  );
  const [copied, setCopied] = useState(false);

  // Use only real slug from database
  const salonSlug = realSlug || "";

  // Build the complete URL
  const storefrontUrl = buildStorefrontUrl(selectedLanguage, salonSlug, trafficSourceCode);

  const handleCopyUrl = useCallback(async () => {
    if (!storefrontUrl) return;

    try {
      await navigator.clipboard.writeText(storefrontUrl);
      setCopied(true);
      toast.success(t("trafficSources.urlCopy.success"));

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(t("trafficSources.urlCopy.error"));
      console.error("Failed to copy URL:", error);
    }
  }, [storefrontUrl, t]);

  const handleOpenUrl = useCallback(() => {
    if (storefrontUrl) {
      window.open(storefrontUrl, "_blank", "noopener,noreferrer");
    }
  }, [storefrontUrl]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            {t("trafficSources.urlCopy.title")}
          </DialogTitle>
          <DialogDescription>
            {trafficSourceName
              ? t("trafficSources.urlCopy.descriptionWithSource", {
                  sourceName: trafficSourceName,
                })
              : t("trafficSources.urlCopy.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Language Selection */}
          <div className="space-y-2">
            <Label htmlFor="language-select">{t("trafficSources.urlCopy.language")}</Label>
            <Select
              value={selectedLanguage}
              onValueChange={(value) => setSelectedLanguage(value as Language)}
            >
              <SelectTrigger id="language-select">
                <SelectValue>
                  {selectedLanguage && (
                    <div className="flex items-center gap-2">
                      <span>
                        {LANGUAGE_INFO[selectedLanguage].name} (
                        {LANGUAGE_INFO[selectedLanguage].nativeName})
                      </span>
                      {selectedLanguage === salonDefaultLanguage && (
                        <Badge variant="secondary" className="text-xs">
                          {t("trafficSources.urlCopy.default")}
                        </Badge>
                      )}
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {Object.values(Language).map((language) => (
                  <SelectItem key={language} value={language}>
                    <div className="flex items-center gap-2">
                      <span>
                        {LANGUAGE_INFO[language].name} ({LANGUAGE_INFO[language].nativeName})
                      </span>
                      {language === salonDefaultLanguage && (
                        <Badge variant="secondary" className="text-xs">
                          {t("trafficSources.urlCopy.default")}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* URL Preview */}
          <div className="space-y-2">
            <Label htmlFor="url-input">{t("trafficSources.urlCopy.generatedUrl")}</Label>
            <div className="flex gap-2">
              <Input
                id="url-input"
                value={loadingSlug ? "Loading..." : storefrontUrl}
                readOnly
                className="font-mono text-sm"
                disabled={loadingSlug}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyUrl}
                disabled={loadingSlug || !storefrontUrl}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleCopyUrl}
              disabled={loadingSlug || !storefrontUrl}
              className="flex-1"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t("trafficSources.urlCopy.copied")}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {t("trafficSources.urlCopy.copyUrl")}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenUrl}
              disabled={loadingSlug || !storefrontUrl}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t("trafficSources.urlCopy.openUrl")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
